import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose, MdInfoOutline } from "react-icons/md";
import CarrierModal from "../CarrierModal";
import { useSnackBar } from "../../../../../../../hooks/useSnackBar";
import { addCustomCarrier } from "../../../../../../../services/ShipperServices/Carriers/carriers.services";
import { toCamelCase } from "../../../../../../../helpers/helpers";
import CarrierLogo from "../CarrierLogo";
import { carrierDisplayName } from "../../carrierHelpers";
import { CONNECT_FORMS, connectableCarriers, emptyPayload } from "./connectForms";
import { CONNECT_PREVIEW } from "../ComingSoon/upcomingCarriers";

const ConnectAccountDialog = ({ upcoming, onClose, onConnected }) => {
  const { t } = useTranslation("settings");
  const notify = useSnackBar();
  const carriers = useMemo(connectableCarriers, []);
  const comingSoon = upcoming
    .flatMap((group) => group.items)
    .filter((carrier) => CONNECT_PREVIEW.includes(carrier.name));
  const [picked, setPicked] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const form = picked && CONNECT_FORMS[picked.id];

  const pick = (carrier) => {
    setPicked(carrier);
    setValues({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await addCustomCarrier({
      ...emptyPayload(form),
      ...values,
      company_name: toCamelCase(picked.name),
    });
    setSaving(false);
    if (error) {
      notify.error(error?.response?.data?.message);
      return;
    }
    notify.success(
      t("carriers.connect.connected", { name: carrierDisplayName(picked.name) }),
    );
    onConnected();
    onClose();
  };

  return (
    <CarrierModal
      onClose={saving ? undefined : onClose}
      boxClassName="!w-[560px] max-w-[94%] max-h-[90vh] !rounded-[14px] !p-0 shadow-ui-md"
    >
      <form onSubmit={handleSubmit} autoComplete="off" className="flex max-h-[90vh] flex-col">
        <div className="flex items-center gap-2.5 border-b border-ui-border px-[16px] py-[18px] sm:px-[22px]">
          <div className="flex-1 text-[17px] font-semibold text-ui-text">
            {picked
              ? t("carriers.connect.title", { name: carrierDisplayName(picked.name) })
              : t("carriers.connect.pick")}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={t("common:actions.close")}
            className="flex h-9 w-9 items-center justify-center rounded-full text-ui-text-muted hover:bg-ui-surface-sunken"
          >
            <MdClose className="h-[22px] w-[22px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-[16px] py-[18px] sm:px-[22px]">
          {!picked &&
            (carriers.length ? (
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {carriers.map((carrier) => (
                  <button
                    key={carrier.id}
                    type="button"
                    onClick={() => pick(carrier)}
                    className="flex items-center gap-2.5 rounded-[10px] border border-ui-border bg-ui-surface px-3 py-2.5 text-left transition-colors hover:border-brand"
                  >
                    <CarrierLogo src={carrier.logo_url} name={carrierDisplayName(carrier.name)} size="sm" />
                    <span className="min-w-0 flex-1 text-[13px] font-semibold text-ui-text">
                      {carrierDisplayName(carrier.name)}
                    </span>
                  </button>
                ))}
                {comingSoon.map((carrier) => (
                  <div
                    key={carrier.name}
                    aria-disabled="true"
                    className="flex cursor-not-allowed items-center gap-2.5 rounded-[10px] border border-ui-border bg-ui-surface-sunken px-3 py-2.5"
                  >
                    <CarrierLogo
                      src={carrier.logo}
                      name={carrier.name}
                      code={carrier.code}
                      size="sm"
                      tone="muted"
                      dimmed
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold text-ui-text-muted">
                        {carrier.name}
                      </span>
                      <span className="block text-[11px] text-ui-text-muted">{t("carriers.comingSoon")}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-[13px] text-ui-text-muted">
                {t("carriers.connect.none")}
              </div>
            ))}

          {form && (
            <div className="flex flex-col gap-3.5">
              {form.fields.map(({ name, label, type, placeholder, i18nKey, placeholderKey }) => (
                <label
                  key={name}
                  className="flex flex-col items-stretch gap-1 text-left text-xs font-medium text-state-neutral-text"
                >
                  {t(i18nKey, { defaultValue: label })}
                  <input
                    type={type}
                    name={name}
                    required
                    placeholder={t(placeholderKey ?? i18nKey, {
                      defaultValue: placeholder ?? label,
                    })}
                    autoComplete={type === "password" ? "new-password" : "off"}
                    value={values[name] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [name]: e.target.value }))}
                    className="h-10 rounded-lg border border-ui-border bg-ui-surface px-3 text-[13px] text-ui-text outline-none placeholder:text-ui-text-subtle focus:border-brand focus:shadow-[0_0_0_2px_var(--color-brand)]"
                  />
                </label>
              ))}
              <div className="flex gap-2 rounded-[10px] bg-ui-surface-sunken p-3 text-xs leading-relaxed text-state-neutral-text">
                <MdInfoOutline className="h-4 w-4 shrink-0 text-brand" />
                <span>{form.requirement}</span>
              </div>
            </div>
          )}
        </div>

        {form && (
          <div className="flex justify-between gap-2 border-t border-ui-border px-[16px] py-3.5 sm:px-[22px]">
            <button
              type="button"
              onClick={() => setPicked(null)}
              disabled={saving}
              className="h-[38px] rounded-lg border border-ui-border bg-ui-surface px-4 text-[13px] text-ui-text-secondary"
            >
              {t("common:actions.back")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="h-[38px] rounded-lg bg-brand px-[18px] text-[13px] font-medium text-brand-contrast hover:bg-brand-hover disabled:cursor-wait disabled:opacity-60"
            >
              {saving ? t("carriers.connect.connecting") : t("carriers.connect.connect")}
            </button>
          </div>
        )}
      </form>
    </CarrierModal>
  );
};

export default ConnectAccountDialog;
